import { init, RematchRootState, RematchDispatch, Models, InitConfig } from '@rematch/core';
import immerPlugin from '@rematch/immer';
import loadingPlugin, { ExtraModelsFromLoading } from '@rematch/loading';
import * as Sentry from '@sentry/browser';

import createSentryMiddleware from 'redux-sentry-middleware';

import { admin } from 'src/modules/admin/rematchModels/admin';
import { assessments } from 'src/modules/assessments/rematchModels/assessments';
import { authentication } from 'src/modules/auth/rematchModels/authentication';
import { countries } from 'src/modules/countries/rematchModels/countries';
import { goalCategories } from 'src/modules/goals/rematchModels/goalCategories';
import { goalInterests } from 'src/modules/goals/rematchModels/goalInterests';
import { languages } from 'src/modules/languages/rematchModels/languages';
import { providers } from 'src/modules/providers/rematchModels/providers';
import { user } from 'src/modules/user/rematchModels/user';
import { videoSessions } from 'src/modules/videoSessions/rematchModels/videoSessions';
import { messaging } from 'src/modules/messaging/rematchModels/messaging';

const sentryMiddleware = createSentryMiddleware(Sentry);

export interface RootModel extends Models<RootModel> {
    authentication: typeof authentication;
    providers: typeof providers;
    goalCategories: typeof goalCategories;
    assessments: typeof assessments;
    goalInterests: typeof goalInterests;
    user: typeof user;
    videoSessions: typeof videoSessions;
    countries: typeof countries;
    languages: typeof languages;
    admin: typeof admin;
    messaging: typeof messaging;
}

export const models: RootModel = {
    authentication,
    providers,
    goalCategories,
    assessments,
    goalInterests,
    user,
    videoSessions,
    countries,
    languages,
    admin,
    messaging,
};

export type FullModel = ExtraModelsFromLoading<RootModel>;

export const getInitStoreConfig = (): InitConfig<RootModel, FullModel> => ({
    models,
    redux: {
        middlewares: [sentryMiddleware],
    },
    plugins: [immerPlugin(), loadingPlugin()],
});

export const store = init<RootModel, FullModel>(getInitStoreConfig());

export type Store = typeof store;
export type Dispatch = RematchDispatch<RootModel>;
export type RootState = RematchRootState<RootModel, FullModel>;

/**
 * Removes "[x: string]: any;" from the source type
 * Source code: stackoverflow.com/a/51955852/2887218
 */
type RemoveIndex<T> = {
    [K in keyof T as string extends K ? never : number extends K ? never : K]: T[K];
};

/**
 * Use ExplicitRootState (instead of RootState) for mocking/testing purposes
 */
export type ExplicitRootState = RemoveIndex<RootState>;
